import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Sun, Waves, Mountain } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function DestinationsPage() {
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(`${API}/destinations`);
        setDestinations(response.data);
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    };
    fetchDestinations();
  }, []);

  const getIcon = (index) => {
    const icons = [Sun, Waves, Mountain, MapPin];
    const Icon = icons[index % icons.length];
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div data-testid="destinations-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/2583852/pexels-photo-2583852.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Indonesia paradise"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E2954]/90 to-[#0E2954]/60" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style text-white/70 mb-4">
              Explore Indonesia
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl text-white max-w-2xl">
              Iconic Destinations Across the Archipelago
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-24"
          >
            {destinations.map((dest, index) => (
              <motion.div
                key={dest.id}
                variants={fadeInUp}
                data-testid={`destination-detail-${dest.id}`}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "md:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "md:order-2" : ""}>
                  <div className="overflow-hidden rounded-lg">
                    <img 
                      src={dest.image_url}
                      alt={dest.name}
                      className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? "md:order-1" : ""}>
                  <span className="label-style text-[#1B4D3E] mb-4 block">
                    Featured Destination
                  </span>
                  <h2 className="text-3xl md:text-5xl mb-6">{dest.name}</h2>
                  <p className="text-[#666666] text-lg leading-relaxed mb-8">
                    {dest.description}
                  </p>
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0E2954]">Highlights</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {dest.highlights.map((highlight, i) => (
                        <div 
                          key={i}
                          className="flex items-center gap-3 p-4 bg-[#FAFAFA] rounded-lg"
                        >
                          <div className="w-10 h-10 bg-[#1B4D3E]/10 rounded-full flex items-center justify-center text-[#1B4D3E]">
                            {getIcon(i)}
                          </div>
                          <span className="text-sm font-medium">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="section-padding bg-[#0E2954]">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl text-white mb-12 text-center">
              Travel Tips for Indonesia
            </motion.h2>
            <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Best Time to Visit",
                  description: "April to October offers dry season with perfect weather for outdoor activities and island hopping."
                },
                {
                  title: "Local Etiquette",
                  description: "Dress modestly when visiting temples. Remove shoes before entering homes and sacred places."
                },
                {
                  title: "Getting Around",
                  description: "Domestic flights connect major islands. Ferries and local boats perfect for island exploration."
                }
              ].map((tip, index) => (
                <div key={index} className="bg-white/5 rounded-lg p-8">
                  <h3 className="text-white text-xl mb-4">{tip.title}</h3>
                  <p className="text-white/70">{tip.description}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
